import { useState } from 'react';
import { CalendarCheck } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/Toast';
import { PRACTICE_TIME_ZONES, timeZoneName } from '@/shared/utils/timezones';
import { useLocation } from '@/shared/hooks/useLocation';
import {
  IntegrationCard,
  type IntegrationStatus,
} from '@/modules/settings/components/IntegrationCard';
import { reviewSmsApi, type OryxConnectionInput } from '../api/reviewSmsApi';
import { reviewKeys } from '../queryKeys';
import { errorMessage, useCanConnectOryx } from './hooks';
import styles from './TextRequests.module.css';

const EMPTY: OryxConnectionInput = { baseUrl: '', username: '', password: '', timezone: '' };
const FORM_ID = 'oryx-connection-form';

const ZONE_OPTIONS = PRACTICE_TIME_ZONES.map((zone) => ({
  value: zone,
  label: timeZoneName(zone),
}));

/** "2026-10-28" -> "Oct 28" without a timezone shift. */
function dayLabel(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Settings → Operations → Practice management system. The current location's Oryx login for the
 * daily appointments pull (backend roster README): Oryx has no API, so each evening we sign in and
 * add the day's finished visits to text review requests. Owners and admins connect it; the password
 * is only ever sent, never shown again. To change the login, disconnect and connect again.
 */
export function OryxIntegration() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const canConnect = useCanConnectOryx();
  const [form, setForm] = useState<OryxConnectionInput>(EMPTY);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const query = useQuery({
    queryKey: reviewKeys.smsOryx(locationId),
    queryFn: () => reviewSmsApi.getOryx(locationId),
    enabled: !!locationId,
  });
  const view = query.data;

  const refresh = () => queryClient.invalidateQueries({ queryKey: reviewKeys.smsOryx(locationId) });
  const save = useMutation({
    mutationFn: (input: OryxConnectionInput) => reviewSmsApi.saveOryx(locationId, input),
    onSuccess: () => {
      refresh();
      setForm(EMPTY);
      setFormOpen(false);
      toast.success('Oryx connected. Finished visits are added each evening at 6 pm.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const disconnect = useMutation({
    mutationFn: () => reviewSmsApi.disconnectOryx(locationId),
    onSuccess: () => {
      refresh();
      setConfirmDisconnect(false);
      toast.success('Oryx disconnected. Visits already added stay.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  if (!view) return null;

  const status: IntegrationStatus = !view.connected
    ? 'DISCONNECTED'
    : view.status === 'error'
      ? 'ERROR'
      : 'CONNECTED';
  const metadata = [
    ...(view.host ? [{ label: 'Address', value: view.host }] : []),
    ...(view.timezone ? [{ label: 'Time zone', value: timeZoneName(view.timezone) }] : []),
    ...(view.lastRun
      ? [
          {
            label: 'Last pull',
            value: `${dayLabel(view.lastRun.day)}, ${view.lastRun.rostered} finished ${
              view.lastRun.rostered === 1 ? 'visit' : 'visits'
            }, ${view.lastRun.consented} can be asked for a review`,
          },
        ]
      : view.connected && !view.lastError
        ? [{ label: 'Last pull', value: 'none yet; the first runs this evening' }]
        : []),
  ];
  const set =
    (field: keyof OryxConnectionInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate({ ...form, baseUrl: form.baseUrl.trim(), username: form.username.trim() });
  };
  const complete = form.baseUrl.trim() && form.username.trim() && form.password && form.timezone;
  const locationName = currentLocation?.name ?? 'this location';

  return (
    <>
      <IntegrationCard
        displayName="Oryx"
        description={`Oryx has no export we can connect to, so each evening at 6 pm we sign in and add ${locationName}'s finished visits to text review requests. Only patients Oryx marks "OK to text" and "request review" are texted.`}
        icon={CalendarCheck}
        status={status}
        metadata={metadata}
        errorMessage={
          view.lastError
            ? `The pull for ${dayLabel(view.lastError.day)} failed: ${view.lastError.message}. It tries again tomorrow evening.`
            : undefined
        }
        managedNote={canConnect ? undefined : 'Ask an owner or admin to connect Oryx.'}
        onConnect={canConnect ? () => setFormOpen(true) : undefined}
        onDisconnect={canConnect ? () => setConfirmDisconnect(true) : undefined}
        disconnectLoading={disconnect.isPending}
      />

      <Modal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setForm(EMPTY);
        }}
        title="Connect Oryx"
        size="md"
        disableDismiss={save.isPending}
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setFormOpen(false);
                setForm(EMPTY);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              size="sm"
              isLoading={save.isPending}
              disabled={!complete}
            >
              Connect Oryx
            </Button>
          </>
        }
      >
        <form id={FORM_ID} className={styles.visitForm} onSubmit={submit} noValidate>
          <p className={styles.muted}>
            Use the front desk login you use to run Oryx reports. We store it encrypted and never
            show it again.
          </p>
          <Input
            id="oryx-base-url"
            label="Oryx address"
            placeholder="https://yourpractice.myoryx.com/kois/index.html"
            value={form.baseUrl}
            onChange={set('baseUrl')}
            autoComplete="off"
            required
          />
          <Input
            id="oryx-username"
            label="Username"
            value={form.username}
            onChange={set('username')}
            autoComplete="off"
            required
          />
          <Input
            id="oryx-password"
            label="Password"
            type="password"
            value={form.password}
            onChange={set('password')}
            autoComplete="new-password"
            required
          />
          <Select
            id="oryx-timezone"
            label="Practice time zone"
            placeholder="Choose…"
            options={ZONE_OPTIONS}
            value={form.timezone}
            onChange={set('timezone')}
            required
          />
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={() => disconnect.mutate()}
        title="Disconnect Oryx?"
        message="We stop adding visits each evening and forget the login. Visits already added stay."
        confirmLabel="Disconnect"
        variant="destructive"
        isLoading={disconnect.isPending}
      />
    </>
  );
}
